import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/lib/supabaseClient';

export type Conversation = {
  matchId: string;
  partnerUserId: string;
  partnerName: string;
  partnerRole: 'patient' | 'caregiver';
  lastMessage: string | null;
  lastMessageTime: string | null;
  lastSenderId: string | null;
  unreadCount: number;
  matchNumber: string | null;
};

export function useChatList(userId: string | null) {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [totalUnread, setTotalUnread] = useState(0);
  const [loading, setLoading] = useState(true);
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const subscribedRef = useRef(false);
  const matchIdsRef = useRef<Set<string>>(new Set());

  const loadConversations = useCallback(async () => {
    if (!userId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // 1. 수락된 매칭 조회
      const { data: matches, error: matchError } = await supabase
        .from('match_requests')
        .select('id, requester_id, receiver_id, requester_role, receiver_role, match_number')
        .eq('status', 'accepted')
        .or(`requester_id.eq.${userId},receiver_id.eq.${userId}`)
        .order('updated_at', { ascending: false });

      if (matchError) throw matchError;

      // 현재 사용자의 매칭 ID 저장 (실시간 구독 필터링용)
      matchIdsRef.current = new Set((matches || []).map((m) => m.id));

      if (!matches || matches.length === 0) {
        setConversations([]);
        setTotalUnread(0);
        setLoading(false);
        return;
      }

      // 2. 각 매칭별로 상대방 정보 + 마지막 메시지 + 안읽은 수 조회
      const conversationPromises = matches.map(async (match) => {
        const isRequester = match.requester_id === userId;
        const partnerUserId = isRequester ? match.receiver_id : match.requester_id;
        const partnerRole = (isRequester ? match.receiver_role : match.requester_role) as 'patient' | 'caregiver';

        // 상대방 프로필
        const { data: partnerProfile } = await supabase
          .from('profiles')
          .select('name')
          .eq('id', partnerUserId)
          .maybeSingle();

        // 안읽은 메시지 수
        const { count: unreadCount } = await supabase
          .from('messages')
          .select('*', { count: 'exact', head: true })
          .eq('match_id', match.id)
          .eq('is_read', false)
          .neq('sender_id', userId);

        // 마지막 메시지
        const { data: lastMessages } = await supabase
          .from('messages')
          .select('content, sender_id, created_at')
          .eq('match_id', match.id)
          .order('created_at', { ascending: false })
          .limit(1);

        const lastMsg = lastMessages && lastMessages.length > 0 ? lastMessages[0] : null;

        return {
          matchId: match.id,
          partnerUserId,
          partnerName: partnerProfile?.name || '알 수 없음',
          partnerRole,
          lastMessage: lastMsg?.content || null,
          lastMessageTime: lastMsg?.created_at || null,
          lastSenderId: lastMsg?.sender_id || null,
          unreadCount: unreadCount || 0,
          matchNumber: match.match_number || null,
        };
      });

      const results = await Promise.all(conversationPromises);
      setConversations(results);
      setTotalUnread(results.reduce((sum, c) => sum + c.unreadCount, 0));
    } catch (err) {
      console.error('Failed to load conversations:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // 실시간 새 메시지 감지 → 현재 사용자 매칭에만 반응
  useEffect(() => {
    if (!userId) return;
    if (subscribedRef.current) return;
    subscribedRef.current = true;

    const channel = supabase
      .channel('chat-list-updates')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'messages' },
        (payload) => {
          const msgMatchId = (payload.new as { match_id: string }).match_id;
          if (matchIdsRef.current.has(msgMatchId)) {
            loadConversations();
          }
        }
      )
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'messages' },
        (payload) => {
          const msgMatchId = (payload.new as { match_id: string }).match_id;
          if (matchIdsRef.current.has(msgMatchId)) {
            loadConversations();
          }
        }
      )
      .subscribe();

    channelRef.current = channel;

    return () => {
      channel.unsubscribe();
      channelRef.current = null;
      subscribedRef.current = false;
    };
  }, [userId, loadConversations]);

  return { conversations, totalUnread, loading, refresh: loadConversations };
}